import { Button, Collapse, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material'
import React, { useState } from 'react'
import { currentWellDetailsSelector, currentWellSelector, wellViewsOpenAtom } from 'atoms'
import useInnovaTheme, { NAVBAR_HEIGHT } from 'components/common/hooks/useInnovaTheme'
import { useRecoilState, useSetRecoilState } from 'recoil'

import { Box } from '@mui/material'
import CasingActiveIcon from 'assets/wellScreenIcons/schematicBlue.png'
import CasingDefaultIcon from 'assets/wellScreenIcons/schematicGrey.png'
import CasingDefaultIconLight from 'assets/wellScreenIcons/schematicBlack.png'
import CostsActiveIcon from 'assets/wellScreenIcons/currency-usd-blue.png'
import CostsDefaultIcon from 'assets/wellScreenIcons/currency-usd-grey.png'
import CostsDefaultIconLight from 'assets/wellScreenIcons/currency-usd-black.png'
import DailyActivityActiveIcon from 'assets/wellScreenIcons/daily-report-blue.png'
import DailyActivityDefaultIcon from 'assets/wellScreenIcons/daily-report-grey.png'
import DailyActivityDefaultIconLight from 'assets/wellScreenIcons/daily-report-black.png'
import DrillStringActiveIcon from 'assets/wellScreenIcons/drill-string-blue.png'
import DrillStringDefaultIcon from 'assets/wellScreenIcons/drill-string-grey.png'
import DrillStringDefaultIconLight from 'assets/wellScreenIcons/drill-string-black.png'
import EdrActiveIcon from 'assets/wellScreenIcons/edr-blue.png'
import EdrDefaultIcon from 'assets/wellScreenIcons/edr-grey.png'
import EdrDefaultIconLight from 'assets/wellScreenIcons/edr-black.png'
import EngineeringActiveIcon from 'assets/wellScreenIcons/engineering-blue.png'
import EngineeringDefaultIcon from 'assets/wellScreenIcons/engineering-gray.png'
import EngineeringDefaultIconLight from 'assets/wellScreenIcons/engineering-black.png'
import { Icon as Iconify } from '@iconify/react'
import LogisticsActiveIcon from 'assets/wellScreenIcons/logistics-flatbed-blue.png'
import LogisticsDefaultIcon from 'assets/wellScreenIcons/logistics-flatbed-grey.png'
import LogisticsDefaultIconLight from 'assets/wellScreenIcons/logistics-flatbed-black.png'
import { ROUTES } from 'components/routes'
import SurveysActiveIcon from 'assets/wellScreenIcons/surveys-blue.png'
import SurveysDefaultIcon from 'assets/wellScreenIcons/surveys-grey.png'
import SurveysDefaultIconLight from 'assets/wellScreenIcons/surveys-black.png'
import ToolInventoryActiveIcon from 'assets/wellScreenIcons/inventory-blue.png'
import ToolInventoryDefaultIcon from 'assets/wellScreenIcons/inventory-grey.png'
import ToolInventoryDefaultIconLight from 'assets/wellScreenIcons/inventory-black.png'
import WellListActiveIcon from 'assets/wellScreenIcons/view-list-blue.png'
import WellListDefaultIcon from 'assets/wellScreenIcons/view-list-grey.png'
import WellListDefaultIconLight from 'assets/wellScreenIcons/view-list-black.png'
import WellViewsActiveIcon from '../../assets/WellIcons/wellIconActive.png'
import WellViewsDefaultIcon from '../../assets/WellIcons/wellIconWhite.png'
import WellViewsDefaultIconLight from '../../assets/WellIcons/wellIconBlack.png'
import { appColors } from 'utils'
import { checkFeature } from 'components/userPermissions'
import { useAppSettings } from 'components/contexts/AppSettingContext'
import { useAuth0 } from '@auth0/auth0-react'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import { useNavigate } from 'react-router-dom'
import useUserDatabaseAccess from 'components/common/hooks/useUserDatabaseAccess'

const DRAWERWIDE = 160
const DRAWERSLIM = 60
const ACTIVE_COLOR = '#34c9eb'
const HOVER_COLOR = '#2aa7c3'

const ActionBar = () => {
  const { actionBarWidth: drawerWidth, setActionBarWidth: setDrawerWidth } = useAppSettings()
  const [wellListOpen, setWellsListOpen] = useRecoilState(wellViewsOpenAtom)
  const { isAuthenticated } = useAuth0()
  const navigate = useNavigate()
  const { theme, getActionBarColor, getTextColor, getAgGridTheme } = useInnovaTheme()
  const { getDefaultDatabaseOrg, saveDatabaseOrg, userRole, changeUserRole } = useInnovaAuth()
  const { hasMultiDbAccess, setAccessDatabase } = useUserDatabaseAccess(false)
  const [currentWell, setCurrentWell] = useRecoilState(currentWellSelector)
  const setCurrentWellDetails = useSetRecoilState(currentWellDetailsSelector)

  if (!isAuthenticated) return null

  const isWellPage = (route) => {
    const matchedRoute = Object.values(ROUTES).find(({ url }) => url === route)

    return matchedRoute?.isWellView ?? false
  }

  const handleNavigateMultiOrg = (route) => {
    if (userRole.innovaAdmin) return navigate(route)
    if (route === ROUTES.databaseTree.url) return navigate(route) // already handled

    if (isWellPage(window.location.pathname) && currentWell.organization) {
      if (userRole.organization !== currentWell.organization) {
        setAccessDatabase(currentWell.organization)
        changeUserRole(currentWell.organization)
        saveDatabaseOrg(currentWell.organization)
      }

      return navigate(route)
    }

    const defaultOrg = getDefaultDatabaseOrg()
    if (defaultOrg === userRole.organization) return navigate(route) // already on default org

    setAccessDatabase(defaultOrg)
    changeUserRole(defaultOrg)
    saveDatabaseOrg(defaultOrg)

    return navigate(route)
  }

  const handleActionClick = (route) => {
    handleNavigateMultiOrg(route)
  }

  const ExpandContractButton = () => {
    const { getTooltipBgColor } = useInnovaTheme()
    const tooltipTitle = drawerWidth === DRAWERWIDE ? 'Collapse' : 'Expand'
    const arrowIcon = drawerWidth === DRAWERWIDE ? 'icon-park-outline:left' : 'icon-park-outline:right'

    return (
      <Button
        onClick={() => setDrawerWidth((prevWidth) => (prevWidth === DRAWERWIDE ? DRAWERSLIM : DRAWERWIDE))}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          padding: 0,
          paddingRight: drawerWidth === DRAWERWIDE ? 0 : 2,
        }}>
        <Tooltip
          title={tooltipTitle}
          placement='right'
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: getTooltipBgColor(),
                fontSize: '12px',
                fontFamily: 'Roboto',
              },
            },
          }}>
          <Iconify icon={arrowIcon} style={{ color: getTextColor(), height: 16, width: 16 }} />
        </Tooltip>
      </Button>
    )
  }

  const ListItem = ({
    image,
    imageHover,
    height = 28,
    width = 28,
    text,
    route,
    onClick,
    isVisible = true,
    isNested = false,
    selected,
    isMenu = false,
    isOpen = false,
  }) => {
    const [isHovered, setIsHovered] = useState(false)
    if (!isVisible) return null

    const isSelected = !isMenu && (selected ?? (route != null && route === window.location.pathname))
    const isDark = theme === 'dark'
    const textColor = isSelected ? ACTIVE_COLOR : isDark ? appColors.headerTextColor : '#151515'
    const handleClick = onClick ?? (() => handleActionClick(route))
    const isImage =
      typeof image === 'string' &&
      (image.includes('data:image') || image.includes('/static/') || /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(image))
    const imgSrc = isImage ? (isHovered && imageHover ? imageHover : image) : undefined

    const iconSize = isNested ? 20 : 24
    const iconComponent = isImage ? (
      <img
        alt=''
        src={imgSrc}
        style={{
          height: Math.min(height, iconSize),
          width: Math.min(width, iconSize),
          objectFit: 'contain',
          display: 'block',
        }}
      />
    ) : (
      <Iconify icon={image} style={{ height: Math.min(height, iconSize), width: Math.min(width, iconSize) }} />
    )

    const itemSx = {
      position: 'relative',
      boxSizing: 'border-box',
      mx: isNested ? (drawerWidth === DRAWERWIDE ? '5px' : '2px') : '4px',
      my: isNested ? '1px' : '2px',
      px: isNested ? (drawerWidth === DRAWERWIDE ? '5px' : '2px') : drawerWidth === DRAWERWIDE ? '5px' : '4px',
      py: isNested ? '3px' : '5px',
      minHeight: isNested ? 39 : 46,
      gap: drawerWidth === DRAWERWIDE ? (isNested ? '6px' : '8px') : 0,
      justifyContent: drawerWidth === DRAWERWIDE ? 'flex-start' : 'center',
      borderRadius: isNested ? '6px' : '7px',
      transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '20%',
        height: '60%',
        width: isNested ? '2px' : '3px',
        borderRadius: '0 3px 3px 0',
        backgroundColor: ACTIVE_COLOR,
        opacity: isSelected ? 1 : 0,
        transform: isSelected ? 'scaleY(1)' : 'scaleY(0.4)',
        transition: 'opacity 0.2s ease, transform 0.2s ease',
      },
      '&.Mui-selected': {
        backgroundColor: isDark ? 'rgba(52, 201, 235, 0.08)' : 'rgba(52, 201, 235, 0.06)',
      },
      '&:hover, &.Mui-selected:hover': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.045)',
      },
      '&:hover .MuiListItemIcon-root': {
        color: HOVER_COLOR,
        borderColor: HOVER_COLOR,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16)',
      },

      ...(isMenu && {
        '&:hover': {
          backgroundColor: isDark ? 'rgba(52, 201, 235, 0.045)' : 'rgba(52, 201, 235, 0.035)',
        },
        '&:hover .MuiListItemIcon-root': {
          borderColor: 'rgba(52, 201, 235, 0.45)',
          boxShadow: 'none',
        },
      }),

      ...(isNested && {
        mx: drawerWidth === DRAWERSLIM ? '4px' : '6px',
        my: '3px',
        px: drawerWidth === DRAWERSLIM ? '4px' : '6px',
        py: '4px',
        minHeight: 40,
        borderRadius: '7px',
        gap: drawerWidth === DRAWERSLIM ? 0 : '8px',
        justifyContent: drawerWidth === DRAWERSLIM ? 'center' : 'flex-start',
      }),
    }

    const iconSx = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: isNested ? 30 : 36,
      height: isNested ? 30 : 36,
      minWidth: isNested ? 30 : 36,
      borderRadius: isNested ? '6px' : '8px',
      boxSizing: 'border-box',
      color: textColor,
      backgroundColor: isSelected
        ? 'rgba(52, 201, 235, 0.10)'
        : isDark
          ? 'rgba(255, 255, 255, 0.035)'
          : 'rgba(0, 0, 0, 0.025)',
      border: '1px solid',
      borderColor: isSelected
        ? 'rgba(52, 201, 235, 0.45)'
        : isDark
          ? 'rgba(255, 255, 255, 0.10)'
          : 'rgba(0, 0, 0, 0.10)',
      boxShadow:
        isNested || isMenu
          ? 'none'
          : isSelected
            ? '0 2px 8px rgba(52, 201, 235, 0.12)'
            : '0 1px 3px rgba(0, 0, 0, 0.10)',
      transition: 'all 0.2s ease',

      ...(isMenu && {
        borderStyle: 'solid',
        borderColor: isOpen ? 'rgba(52, 201, 235, 0.35)' : isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
        backgroundColor: isOpen ? 'rgba(52, 201, 235, 0.04)' : 'transparent',
        boxShadow: 'none',
      }),

      '& svg': { color: 'currentColor', fill: 'currentColor' },

      ...(isNested && {
        width: 32,
        height: 32,
        minWidth: 32,
        borderRadius: '7px',

        backgroundColor: isSelected ? 'rgba(52, 201, 235, 0.10)' : 'transparent',

        borderColor: isSelected
          ? 'rgba(52, 201, 235, 0.45)'
          : isDark
            ? 'rgba(255, 255, 255, 0.10)'
            : 'rgba(0, 0, 0, 0.10)',

        boxShadow: isSelected ? '0 2px 6px rgba(52, 201, 235, 0.10)' : 'none',
      }),
    }

    const iconNode = <ListItemIcon sx={iconSx}>{iconComponent}</ListItemIcon>

    return (
      <ListItemButton
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        selected={isSelected}
        onClick={handleClick}
        sx={itemSx}>
        {drawerWidth === DRAWERWIDE ? (
          iconNode
        ) : (
          <Tooltip
            title={isMenu ? `${text} — ${isOpen ? 'Collapse menu' : 'Expand menu'}` : text}
            placement='right'
            PopperProps={{ style: { zIndex: 20000 } }}>
            {iconNode}
          </Tooltip>
        )}
        {drawerWidth === DRAWERWIDE && (
          <ListItemText
            primary={text}
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: isNested ? '12px' : '14px',
                fontFamily: 'Roboto',
                fontWeight: isNested ? 500 : 'bold',
              },
            }}
          />
        )}

        {isMenu &&
          (drawerWidth === DRAWERWIDE ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                flexShrink: 0,
                borderRadius: '5px',
                backgroundColor: isOpen ? 'rgba(52, 201, 235, 0.15)' : 'rgba(128, 128, 128, 0.12)',
                border: '1px solid',
                borderColor: isOpen ? 'rgba(52, 201, 235, 0.4)' : 'rgba(128, 128, 128, 0.25)',
              }}>
              <Iconify
                icon='lucide:chevron-right'
                width={drawerWidth === DRAWERWIDE ? 14 : 11}
                style={{
                  color: isOpen ? ACTIVE_COLOR : textColor,
                  opacity: 0.85,
                  transform: `rotate(${isOpen ? 90 : 0}deg)`,
                  transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1), color 220ms ease',
                  transformOrigin: 'center',
                  display: 'block',
                  flexShrink: 0,
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                right: 5,
                bottom: 4,
                width: 15,
                height: 15,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                backgroundColor: isDark ? '#24343b' : '#e8f3f6',
                border: '1px solid',
                borderColor: isOpen ? 'rgba(52, 201, 235, 0.55)' : 'rgba(140, 140, 140, 0.45)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
                pointerEvents: 'none',
                zIndex: 2,
                transition: 'all 0.2s ease',
              }}>
              <Iconify
                icon='lucide:chevron-right'
                width={drawerWidth === DRAWERWIDE ? 14 : 11}
                style={{
                  color: isOpen ? ACTIVE_COLOR : textColor,
                  opacity: 0.85,
                  transform: `rotate(${isOpen ? 90 : 0}deg)`,
                  transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1), color 220ms ease',
                  transformOrigin: 'center',
                  display: 'block',
                  flexShrink: 0,
                }}
              />
            </Box>
          ))}
      </ListItemButton>
    )
  }

  return (
    <Drawer
      variant='permanent'
      sx={{
        '& .MuiDrawer-paper': {
          display: 'flex',
          zIndex: (theme) => theme.zIndex.drawer - 1 || 1,
          backgroundColor: getActionBarColor(),
          width: drawerWidth,
          marginTop: `${NAVBAR_HEIGHT}px`,
          height: `calc(100% - ${NAVBAR_HEIGHT}px)`,
        },
      }}>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarGutter: 'auto',
          display: 'flex',
          flexDirection: 'column',

          // Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',

          '&:hover': {
            scrollbarColor: 'rgba(140, 140, 140, 0.35) transparent',
          },

          // Chrome / Edge
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            borderRadius: '10px',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(140, 140, 140, 0.35)',
          },
        }}
        className={getAgGridTheme()}>
        <List sx={{ mt: -0.5 }}>
          <ExpandContractButton />
          <ListItem
            image={
              window.location.pathname === ROUTES.wellList.url
                ? WellListActiveIcon
                : theme === 'dark'
                  ? WellListDefaultIcon
                  : WellListDefaultIconLight
            }
            imageHover={WellListActiveIcon}
            text={'Dashboard'}
            selected={window.location.pathname === ROUTES.wellList.url}
            height={24}
            width={24}
            onClick={() => {
              changeUserRole(getDefaultDatabaseOrg())
              if (!hasMultiDbAccess()) {
                saveDatabaseOrg(getDefaultDatabaseOrg())
              }
              handleActionClick(ROUTES.wellList.url)
            }}
          />
          <ListItem
            image={'clarity:tree-view-solid'}
            text={'Object Explorer'}
            route={ROUTES.databaseTree.url}
            isVisible={checkFeature(26, userRole?.roleAttributes?.featureId)}
            onClick={() => {
              if (!userRole.innovaAdmin) {
                const defaultOrg = getDefaultDatabaseOrg()
                changeUserRole(defaultOrg)
                saveDatabaseOrg(defaultOrg)
                // the below code is just like switching the DB if user is multiDB
                // this prevents the currentWell org from being corrupted.
                setAccessDatabase(defaultOrg)
                setCurrentWell({
                  wellName: '',
                  organization: defaultOrg,
                })
                setCurrentWellDetails({
                  actualWell: '',
                  wellStatus: '',
                  operator: '',
                  rig: '',
                  jobNum: '',
                  lat: '',
                  lng: '',
                })
              }
              handleActionClick(ROUTES.databaseTree.url)
            }}
          />

          <ListItem
            image={theme === 'dark' ? WellViewsDefaultIcon : WellViewsDefaultIconLight}
            imageHover={WellViewsActiveIcon}
            text='Well Views'
            onClick={() => setWellsListOpen(!wellListOpen)}
            isMenu
            isOpen={wellListOpen}
          />

          <Collapse in={wellListOpen} timeout='auto' unmountOnExit>
            <List
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                p: 0,
                my: '4px',
                backgroundColor: 'transparent',

                ...(drawerWidth === DRAWERSLIM
                  ? {
                      mx: 0,
                      pl: '7px',
                      pr: '1px',
                      border: 'none',
                      borderRadius: 0,

                      // Cyan guide line without affecting icon spacing
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '2px',
                        top: '6px',
                        bottom: '6px',
                        width: '2px',
                        borderRadius: '2px',
                        backgroundColor: 'rgba(52, 201, 235, 0.35)',
                        pointerEvents: 'none',
                      },
                    }
                  : {
                      width: 'auto',
                      mx: '6px',
                      px: '4px',
                      py: '5px',
                      borderLeft: '2px solid rgba(52, 201, 235, 0.25)',
                    }),
              }}>
              <ListItem
                image={
                  window.location.pathname === ROUTES.wellDetails.url
                    ? CasingActiveIcon
                    : theme === 'dark'
                      ? CasingDefaultIcon
                      : CasingDefaultIconLight
                }
                imageHover={CasingActiveIcon}
                text={'Well Details'}
                route={ROUTES.wellDetails.url}
                isNested={true}
              />
              <ListItem
                image={
                  window.location.pathname === ROUTES.edr.url
                    ? EdrActiveIcon
                    : theme === 'dark'
                      ? EdrDefaultIcon
                      : EdrDefaultIconLight
                }
                imageHover={EdrActiveIcon}
                text={'EDR'}
                route={ROUTES.edr.url}
                isNested={true}
                isVisible={checkFeature(8, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={'tdesign:chart-area-multi-filled'}
                text={'Views'}
                route={ROUTES.wellInsights.url}
                isNested={true}
                isVisible={checkFeature(8, userRole?.roleAttributes?.featureId)} // *** need a feature flag for this, currently using EDR
              />
              <ListItem
                image={'solar:target-linear'}
                text={'Anti Collision'}
                route={ROUTES.antiCollision.url}
                isNested={true}
                isVisible={checkFeature(3, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={
                  window.location.pathname === ROUTES.dailyReport.url
                    ? DailyActivityActiveIcon
                    : theme === 'dark'
                      ? DailyActivityDefaultIcon
                      : DailyActivityDefaultIconLight
                }
                imageHover={DailyActivityActiveIcon}
                text={'Daily Reports'}
                route={ROUTES.dailyReport.url}
                isNested={true}
                isVisible={checkFeature(35, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={
                  window.location.pathname === ROUTES.inventory.url
                    ? ToolInventoryActiveIcon
                    : theme === 'dark'
                      ? ToolInventoryDefaultIcon
                      : ToolInventoryDefaultIconLight
                }
                imageHover={ToolInventoryActiveIcon}
                text={'Inventory'}
                route={ROUTES.inventory.url}
                isNested={true}
                isVisible={checkFeature(41, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={
                  window.location.pathname === ROUTES.surveys.url
                    ? SurveysActiveIcon
                    : theme === 'dark'
                      ? SurveysDefaultIcon
                      : SurveysDefaultIconLight
                }
                imageHover={SurveysActiveIcon}
                text={'Surveys'}
                route={ROUTES.surveys.url}
                isNested={true}
                isVisible={checkFeature(34, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={
                  window.location.pathname === ROUTES.dailyCost.url
                    ? CostsActiveIcon
                    : theme === 'dark'
                      ? CostsDefaultIcon
                      : CostsDefaultIconLight
                }
                imageHover={CostsActiveIcon}
                text={'Costs'}
                route={ROUTES.dailyCost.url}
                isNested={true}
                width={20}
                height={28}
                isVisible={checkFeature(36, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={
                  window.location.pathname === ROUTES.drillString.url
                    ? DrillStringActiveIcon
                    : theme === 'dark'
                      ? DrillStringDefaultIcon
                      : DrillStringDefaultIconLight
                }
                imageHover={DrillStringActiveIcon}
                text={'Drill String'}
                route={ROUTES.drillString.url}
                isNested={true}
                width={24}
                height={28}
                isVisible={checkFeature(37, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={'gridicons:shipping'}
                text={'Shipping Tickets'}
                route={ROUTES.shippingTickets.url}
                isNested={true}
                isVisible={checkFeature(38, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={'fluent:pipeline-20-regular'}
                text={'Pipe Tallys'}
                route={ROUTES.pipeTallys.url}
                isNested={true}
                isVisible={checkFeature(39, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={
                  window.location.pathname === ROUTES.engineering.url
                    ? EngineeringActiveIcon
                    : theme === 'dark'
                      ? EngineeringDefaultIcon
                      : EngineeringDefaultIconLight
                }
                imageHover={EngineeringActiveIcon}
                text={'Engineering'}
                route={ROUTES.engineering.url}
                isNested={true}
                isVisible={
                  checkFeature(9, userRole?.roleAttributes?.featureId) ||
                  checkFeature(10, userRole?.roleAttributes?.featureId) ||
                  checkFeature(32, userRole?.roleAttributes?.featureId) ||
                  checkFeature(48, userRole?.roleAttributes?.featureId)
                }
              />
              <ListItem
                image={'fa-solid:road'}
                text={'Parameter Roadmap'}
                route={ROUTES.parametersRoadmap.url}
                isNested={true}
                isVisible={checkFeature(43, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={'mdi:pipe-disconnected'}
                text={'Connection Analysis'}
                route={ROUTES.connectionAnalysis.url}
                isNested={true}
                isVisible={checkFeature(2, userRole?.roleAttributes?.featureId)}
              />
              <ListItem
                image={'mdi:land-plots'}
                text={'Wallplot Composer'}
                route={ROUTES.wallplotComposer.url}
                isNested={true}
                isVisible={checkFeature(50, userRole?.roleAttributes?.featureId)}
              />
            </List>
          </Collapse>
          <ListItem
            image={'solar:target-linear'}
            text={'Anti-Collision Dashboard'}
            route={ROUTES.acDashboard.url}
            isVisible={checkFeature(3, userRole?.roleAttributes?.featureId)}
            onClick={() => {
              changeUserRole(getDefaultDatabaseOrg())
              if (!hasMultiDbAccess()) {
                saveDatabaseOrg(getDefaultDatabaseOrg())
              }
              handleActionClick(ROUTES.acDashboard.url)
            }}
          />
          <ListItem
            image={
              window.location.pathname === ROUTES.logistics.url
                ? LogisticsActiveIcon
                : theme === 'dark'
                  ? LogisticsDefaultIcon
                  : LogisticsDefaultIconLight
            }
            imageHover={LogisticsActiveIcon}
            text={'Logistics'}
            route={ROUTES.logistics.url}
            height={20}
            isVisible={checkFeature(42, userRole?.roleAttributes?.featureId)}
            onClick={() => {
              changeUserRole(getDefaultDatabaseOrg())
              if (!hasMultiDbAccess()) {
                saveDatabaseOrg(getDefaultDatabaseOrg())
              }
              handleActionClick(ROUTES.logistics.url)
            }}
          />
          <ListItem
            image={'oi:pie-chart'}
            text={'Data Insights'}
            route={ROUTES.analytics.url}
            width={24}
            onClick={() => {
              changeUserRole(getDefaultDatabaseOrg())
              if (!hasMultiDbAccess()) {
                saveDatabaseOrg(getDefaultDatabaseOrg())
              }
              handleActionClick(ROUTES.analytics.url)
            }}
            isVisible={checkFeature(2, userRole?.roleAttributes?.featureId)}
          />
          <ListItem
            text={'Bids'}
            image={'material-symbols:request-quote-sharp'}
            route={ROUTES.bids.url}
            isVisible={checkFeature(23, userRole?.roleAttributes?.featureId)}
          />
          <ListItem
            text={'Personnel Utilization'}
            image={'mdi:chart-gantt'}
            route={ROUTES.personnelUtilization.url}
            isVisible={checkFeature(20, userRole?.roleAttributes?.featureId)}
          />
          <ListItem
            text={'File Manager'}
            image={'material-symbols:folder-open-outline-rounded'}
            route={ROUTES.fileManager.url}
            isVisible={checkFeature(28, userRole?.roleAttributes?.featureId)}
          />
          <ListItem
            text={'Asset Search'}
            route={ROUTES.assetSearch.url}
            image={'fluent:box-search-20-regular'}
            isVisible={checkFeature(44, userRole?.roleAttributes?.featureId)}
          />
          <ListItem
            text={'Incident Dashboard'}
            route={ROUTES.incidentDashboard.url}
            image={'mdi:monitor-dashboard'}
            isVisible={checkFeature(27, userRole?.roleAttributes?.featureId)}
          />
          <ListItem
            text={'Well Planning'}
            route={ROUTES.wellPlanning.url}
            image={'carbon:tool-kit'}
            isVisible={checkFeature(62, userRole?.roleAttributes?.featureId) || userRole?.innovaAdmin}
          />
        </List>
      </Box>
    </Drawer>
  )
}

export default ActionBar
